import { useCallback, useEffect, useMemo, useState } from 'react';
import { ROWS_PER_PAGE } from 'consts';

const EMPTY_ITEMS = [];
const EMPTY_DATE_SORT_IDS = new Set();

const getQueryState = ({
  queryKeys,
  defaultOrderBy,
  defaultOrder,
  normalizeRowsPerPage,
}) => {
  const params = new URLSearchParams(window.location.search);
  return {
    page: Number(params.get(queryKeys.page)) || 0,
    rowsPerPage: normalizeRowsPerPage(params.get(queryKeys.rowsPerPage)),
    orderBy: params.get(queryKeys.orderBy) || defaultOrderBy,
    order: params.get(queryKeys.order) || defaultOrder,
  };
};

const sortItems = (
  items,
  sort,
  compareValues,
  dateSortIds,
  getDateTimestamp
) => {
  const result = [...items];
  result.sort((left, right) => {
    if (dateSortIds.has(sort.orderBy)) {
      const leftDate = getDateTimestamp(left, sort.orderBy);
      const rightDate = getDateTimestamp(right, sort.orderBy);
      if (leftDate === null || rightDate === null) {
        if (leftDate === rightDate) return 0;
        return leftDate === null ? 1 : -1;
      }
    }
    const comparison = compareValues(left, right, sort.orderBy);
    return sort.order === 'desc' ? -comparison : comparison;
  });
  return result;
};

const defaultNormalizeRowsPerPage = (value) =>
  Number(value) || ROWS_PER_PAGE[0];

const useGuestTransactionList = ({
  items,
  totalRecords,
  queryKeys,
  compareValues,
  dateSortIds = EMPTY_DATE_SORT_IDS,
  getDateTimestamp = () => null,
  defaultOrderBy = 'sendOn',
  defaultOrder = 'desc',
  normalizeRowsPerPage = defaultNormalizeRowsPerPage,
}) => {
  const queryState = useMemo(
    () =>
      getQueryState({
        queryKeys,
        defaultOrderBy,
        defaultOrder,
        normalizeRowsPerPage,
      }),
    [defaultOrder, defaultOrderBy, normalizeRowsPerPage, queryKeys]
  );
  const [sort, setSort] = useState(() => ({
    orderBy: queryState.orderBy,
    order: queryState.order,
  }));
  const [page, setPage] = useState(queryState.page);
  const [rowsPerPage, setRowsPerPage] = useState(queryState.rowsPerPage);
  const listItems = useMemo(() => items || EMPTY_ITEMS, [items]);
  const sortedItems = useMemo(
    () =>
      sortItems(listItems, sort, compareValues, dateSortIds, getDateTimestamp),
    [compareValues, dateSortIds, getDateTimestamp, listItems, sort]
  );
  const pageItems = sortedItems.slice(
    page * rowsPerPage,
    (page + 1) * rowsPerPage
  );
  const listTotal =
    Number.isFinite(totalRecords) && totalRecords >= sortedItems.length
      ? totalRecords
      : sortedItems.length;
  const incomplete =
    Number.isFinite(totalRecords) && totalRecords > sortedItems.length;

  useEffect(() => {
    if (page > 0 && page * rowsPerPage >= sortedItems.length) {
      setPage(0);
    }
  }, [page, rowsPerPage, sortedItems.length]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    params.set(queryKeys.page, String(page));
    params.set(queryKeys.rowsPerPage, String(rowsPerPage));
    params.set(queryKeys.orderBy, sort.orderBy);
    params.set(queryKeys.order, sort.order);
    window.history.replaceState(
      window.history.state,
      '',
      `${window.location.pathname}?${params.toString()}${window.location.hash}`
    );
  }, [page, queryKeys, rowsPerPage, sort]);

  const handleListChange = useCallback(
    (event, next) => {
      const nextOrderBy = next.orderBy || defaultOrderBy;
      const nextOrder = next.order || defaultOrder;
      const nextRowsPerPage = normalizeRowsPerPage(next.rowsPerPage);
      const sortChanged =
        nextOrderBy !== sort.orderBy || nextOrder !== sort.order;
      const pageChanged =
        sortChanged || nextRowsPerPage !== rowsPerPage
          ? 0
          : Number(next.page) || 0;

      setSort({ orderBy: nextOrderBy, order: nextOrder });
      setRowsPerPage(nextRowsPerPage);
      setPage(pageChanged);
    },
    [defaultOrder, defaultOrderBy, normalizeRowsPerPage, rowsPerPage, sort]
  );

  return {
    handleListChange,
    incomplete,
    listItems,
    listTotal,
    page,
    pageItems,
    rowsPerPage,
    sort,
  };
};

export default useGuestTransactionList;
